import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { fmtDateLong } from "@/lib/format";
import { ServiceCalendar, ServiceDetails } from "@/components/ServiceCalendar";
import { CalendarSync } from "@/components/CalendarSync";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Repeat, Loader2 } from "lucide-react";

export default function MyShifts() {
  const [shifts, setShifts] = useState([]);
  const [colleagues, setColleagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // assignment for swap
  const [toUser, setToUser] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/planning/my-shifts"),
        api.get("/users/colleagues"),
      ]);
      setShifts(s.data);
      setColleagues(c.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const requestSwap = async () => {
    if (!toUser) return toast.error("Kies een collega");
    setBusy(true);
    try {
      await api.post("/swaps", { assignment_id: dialog.id, to_user_id: toUser, message });
      toast.success("Ruilverzoek verstuurd");
      setDialog(null); setToUser(""); setMessage("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="my-shifts-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-heading text-3xl font-extrabold tracking-tight">Mijn diensten</h1>
        <p className="text-muted-foreground mt-1">Jouw gepubliceerde geplande diensten.</p></div>
        <CalendarSync />
      </div>

      <ServiceCalendar calendarId="shifts" services={shifts.map((a) => ({ ...a.service, id: a.id, assignment: a }))}
        getStatus={(entry) => ({ tone: "red", label: entry.assignment.service_type_name })}
        renderDetails={(entry) => <ServiceDetails service={entry}>
          <p className="text-sm text-red-700 font-medium" data-testid={`shift-role-${entry.id}`}>{entry.assignment.service_type_name}</p>
          <Button variant="outline" size="sm" onClick={() => setDialog(entry.assignment)} data-testid={`swap-request-${entry.id}`}><Repeat className="w-4 h-4 mr-1.5" />Ruil aanvragen</Button>
        </ServiceDetails>} />

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="swap-dialog">
          <DialogHeader><DialogTitle>Ruil aanvragen</DialogTitle></DialogHeader>
          {dialog && (
            <div className="space-y-4">
              <div className="text-sm bg-muted rounded-lg p-3">
                <p className="font-medium">{dialog.service.name} · {dialog.service_type_name}</p>
                <p className="text-muted-foreground">{fmtDateLong(dialog.service.date)} · {dialog.service.start_time}–{dialog.service.end_time}</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Aan collega</label>
                <Select value={toUser} onValueChange={setToUser}>
                  <SelectTrigger data-testid="swap-colleague-select"><SelectValue placeholder="Kies collega" /></SelectTrigger>
                  <SelectContent>
                    {colleagues.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Textarea placeholder="Bericht (optioneel)" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="swap-message" />
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)}>Annuleren</Button>
            <Button onClick={requestSwap} disabled={busy} data-testid="swap-submit">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Versturen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
